import { FunctionComponent, h } from 'preact';
import { lazy, Suspense } from 'preact/compat';
import { useStore } from '../../helpers/use-store';
import { observer } from 'mobx-react';
import { BaseSharedLocationConfig } from '../../types/shared-location.types';
import { SharedLocationDisplayType, SharingType, TaskStatus } from '@bringg/types';
import { Mobile, TabletAndDesktop } from '../../responsive-devices/responsive-devices';
import Loading from '../../components/loading/loading';
import SharedLocationLayout from '../../pages/shared-location-layout/shared-location-layout';
import { isScheduleShareType } from '../../helpers/sharing-type-helper';

const RatingPage = lazy(() => import('../../pages/rating-page/rating-page'));
const DeliveryScheduledPage = lazy(() => import('../../pages/delivery-scheduled-page/delivery-scheduled-page'));
const DeliveryCompleted = lazy(() => import('../../components/delivery-completed/delivery-completed'));

interface SharedLocationExpiredContentProps {
	sharedLocation: BaseSharedLocationConfig;
}

const ShowRatingScreen = () => (
	<SharedLocationLayout>
		<div className="show-rating-screen" data-testid="show-rating-screen">
			<Mobile>
				<div className="rating-page-container">
					<RatingPage />
				</div>
			</Mobile>
			<TabletAndDesktop>
				<card-component className="rating-page-card">
					<RatingPage />
				</card-component>
			</TabletAndDesktop>
		</div>
	</SharedLocationLayout>
);

const SharedLocationExpiredContent: FunctionComponent<SharedLocationExpiredContentProps> = ({ sharedLocation }) => {
	const {
		shared_type: sharedType,
		allow_rating: allowRating,
		rate_expired: rateExpired,
		display_type: displayType,
		order_status: orderStatus
	} = sharedLocation;
	const shouldShouldRatingScreen = Boolean(
		allowRating && !rateExpired && !isScheduleShareType({ sharedType, displayType })
	);

	if (shouldShouldRatingScreen) {
		if (orderStatus === TaskStatus.Cancelled) {
			return <DeliveryCompleted variant="cancelled" />;
		}
		return <ShowRatingScreen />;
	}

	switch (sharedType) {
		case SharingType.Reschedule:
		case SharingType.Schedule:
			return <DeliveryScheduledPage />;

		case SharingType.CustomWorkflow:
		case SharingType.NotificationByTemplateId:
			return displayType === SharedLocationDisplayType.Schedule ? (
				<DeliveryScheduledPage />
			) : (
				<DeliveryCompleted />
			);

		default:
			return <DeliveryCompleted />;
	}
};

const SharedLocationExpired = observer(() => {
	const { sharedLocationStore } = useStore();

	return (
		<div className="shared-location-expired" data-testid="shared-location-expired">
			<Suspense fallback={<Loading />}>
				<SharedLocationExpiredContent sharedLocation={sharedLocationStore.sharedLocationConfig} />
			</Suspense>
		</div>
	);
});

export default SharedLocationExpired;
